import { UserFacingError } from "../../../utils/userFacingError";

export const memberApplicationOperationVersion = 1;

// These checks run before anything is sent, so nothing was saved. Several
// dashboard saves share them (members, invoices, event sales), so the text
// does not name one screen.
const BROWSER_BLOCKED_SAVE_TEXT =
  "Your browser blocked a security step that this page needs. Turn off private browsing, or use an up-to-date Chrome, Edge, Firefox or Safari. Then try again.";

export type MemberApplicationOperationStorage = Pick<
  Storage,
  "getItem" | "removeItem" | "setItem"
>;

export type PersistedMemberApplicationOperation = {
  fingerprint: string;
  operationId: string;
  version: typeof memberApplicationOperationVersion;
};

export type GetOrCreateMemberApplicationOperationInput = {
  createOperationId?: () => string;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  request: unknown;
  storage?: MemberApplicationOperationStorage;
  storageKey: string;
};

export type ClearMemberApplicationOperationInput = {
  operationId: string;
  storage?: MemberApplicationOperationStorage;
  storageKey: string;
};

export type SingleInFlightSubmissionRef = {
  current: boolean;
};

export type RunSingleMemberApplicationSubmissionInput<Result> = {
  inFlightRef: SingleInFlightSubmissionRef;
  submit: () => Promise<Result>;
};

export type RunSingleMemberApplicationSubmissionResult<Result> =
  | { status: "completed"; value: Result }
  | { status: "ignored-in-flight" };

export type PrepareMemberApplicationSubmissionInput<
  ApplicationInput extends Record<string, unknown>,
> = {
  actorId: string;
  applicationInput: ApplicationInput;
  chamberId: string;
  createOperationId?: () => string;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  storage?: MemberApplicationOperationStorage;
};

export type PreparedMemberApplicationSubmissionInput<
  ApplicationInput extends Record<string, unknown>,
> = {
  input: Omit<ApplicationInput, "operationId"> & { operationId: string };
  operationId: string;
  storageKey: string;
};

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const operationAcquisitionByStorageKey = new Map<string, Promise<unknown>>();

const canonicalizeFingerprintValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => canonicalizeFingerprintValue(item));
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        const item = (value as Record<string, unknown>)[key];

        // Match JSON/GraphQL variable semantics: undefined object properties
        // are omitted, while explicit null remains authority-bearing input.
        if (item !== undefined) {
          result[key] = canonicalizeFingerprintValue(item);
        }

        return result;
      }, {});
  }

  return value;
};

export const canonicalizeMemberApplicationOperationRequest = (
  request: unknown
) => JSON.stringify(canonicalizeFingerprintValue(request));

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

export const fingerprintMemberApplicationOperationRequest = async (
  request: unknown
): Promise<string> => {
  if (!globalThis.crypto?.subtle) {
    // No crypto.subtle (an old browser, or a page not opened over https).
    throw new UserFacingError(BROWSER_BLOCKED_SAVE_TEXT);
  }

  const canonicalRequest = canonicalizeMemberApplicationOperationRequest(
    request
  );
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonicalRequest)
  );

  return bytesToHex(new Uint8Array(digest));
};

export const createSecureMemberApplicationOperationId = (): string => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  if (!globalThis.crypto?.getRandomValues) {
    // No crypto.randomUUID or crypto.getRandomValues.
    throw new UserFacingError(BROWSER_BLOCKED_SAVE_TEXT);
  }

  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytesToHex(bytes);

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

const getDefaultOperationStorage = () => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
};

const parsePersistedMemberApplicationOperation = (
  serializedOperation: string | null
): PersistedMemberApplicationOperation | null => {
  if (!serializedOperation) {
    return null;
  }

  try {
    const operation = JSON.parse(
      serializedOperation
    ) as Partial<PersistedMemberApplicationOperation>;

    if (
      operation.version !== memberApplicationOperationVersion ||
      typeof operation.fingerprint !== "string" ||
      !uuidPattern.test(operation.operationId || "")
    ) {
      return null;
    }

    return operation as PersistedMemberApplicationOperation;
  } catch {
    return null;
  }
};

const readMemberApplicationOperation = ({
  storage,
  storageKey,
}: {
  storage?: MemberApplicationOperationStorage;
  storageKey: string;
}) => {
  try {
    return parsePersistedMemberApplicationOperation(
      storage?.getItem(storageKey) || null
    );
  } catch {
    return null;
  }
};

const persistMemberApplicationOperation = ({
  operation,
  storage,
  storageKey,
}: {
  operation: PersistedMemberApplicationOperation;
  storage?: MemberApplicationOperationStorage;
  storageKey: string;
}) => {
  if (!storage) {
    // No sessionStorage (private mode or blocked site data).
    throw new UserFacingError(BROWSER_BLOCKED_SAVE_TEXT);
  }

  storage.setItem(storageKey, JSON.stringify(operation));
};

const acquireMemberApplicationOperation = async ({
  createOperationId = createSecureMemberApplicationOperationId,
  fingerprintRequest = fingerprintMemberApplicationOperationRequest,
  request,
  storage = getDefaultOperationStorage(),
  storageKey,
}: GetOrCreateMemberApplicationOperationInput) => {
  const fingerprint = await fingerprintRequest(request);
  const persistedOperation = readMemberApplicationOperation({
    storage,
    storageKey,
  });

  if (persistedOperation?.fingerprint === fingerprint) {
    return persistedOperation;
  }

  const operation: PersistedMemberApplicationOperation = {
    fingerprint,
    operationId: createOperationId(),
    version: memberApplicationOperationVersion,
  };

  if (!uuidPattern.test(operation.operationId)) {
    throw new Error("Application-operation identifier must be a valid UUID.");
  }

  persistMemberApplicationOperation({ operation, storage, storageKey });
  return operation;
};

/**
 * Serializes acquisition per chamber/tab so concurrent clicks cannot mint two
 * identifiers for the same request before either session-storage write lands.
 */
export const getOrCreateMemberApplicationOperation = async (
  input: GetOrCreateMemberApplicationOperationInput
): Promise<PersistedMemberApplicationOperation> => {
  const previousAcquisition = operationAcquisitionByStorageKey.get(
    input.storageKey
  );

  const acquisition = (async () => {
    await previousAcquisition;
    return acquireMemberApplicationOperation(input);
  })();

  operationAcquisitionByStorageKey.set(input.storageKey, acquisition);

  try {
    return await acquisition;
  } finally {
    if (
      operationAcquisitionByStorageKey.get(input.storageKey) === acquisition
    ) {
      operationAcquisitionByStorageKey.delete(input.storageKey);
    }
  }
};

/**
 * Clears only the operation acknowledged by the backend. A late response for
 * an older request cannot erase a newer changed-payload operation.
 */
export const clearCompletedMemberApplicationOperation = ({
  operationId,
  storage = getDefaultOperationStorage(),
  storageKey,
}: ClearMemberApplicationOperationInput): boolean => {
  const persistedOperation = readMemberApplicationOperation({
    storage,
    storageKey,
  });

  if (persistedOperation?.operationId !== operationId || !storage) {
    return false;
  }

  try {
    storage.removeItem(storageKey);
    return true;
  } catch {
    // Clearing browser bookkeeping must never reclassify an acknowledged
    // backend application as failed.
    return false;
  }
};

/** Prevents two browser events from dispatching the mutation concurrently. */
export const runSingleMemberApplicationSubmission = async <Result>({
  inFlightRef,
  submit,
}: RunSingleMemberApplicationSubmissionInput<Result>): Promise<
  RunSingleMemberApplicationSubmissionResult<Result>
> => {
  if (inFlightRef.current) {
    return { status: "ignored-in-flight" };
  }

  inFlightRef.current = true;

  try {
    return { status: "completed", value: await submit() };
  } finally {
    inFlightRef.current = false;
  }
};

export const getMemberApplicationOperationStorageKey = ({
  chamberId,
  sourceMemberId,
  sourceProspectId,
}: {
  chamberId: string;
  sourceMemberId?: string;
  sourceProspectId?: string;
}) =>
  sourceProspectId
    ? `cmp-prospect-conversion-operation:v1:${chamberId}:${encodeURIComponent(
        sourceProspectId
      )}`
    : sourceMemberId
      ? `cmp-member-restart-operation:v1:${chamberId}:${encodeURIComponent(
          sourceMemberId
        )}`
    : `cmp-fresh-member-application-operation:v1:${chamberId}`;

/**
 * Every dashboard application, including prospect conversion, receives a
 * persisted operation ID based on the authenticated actor and exact
 * operation-free GraphQL input that will be dispatched. Prospect slots are
 * isolated by source prospect so one in-progress conversion cannot overwrite
 * another application retry.
 */
export const prepareMemberApplicationSubmissionInput = async <
  ApplicationInput extends Record<string, unknown>,
>({
  actorId,
  applicationInput,
  chamberId,
  createOperationId,
  fingerprintRequest,
  storage,
}: PrepareMemberApplicationSubmissionInput<ApplicationInput>): Promise<
  PreparedMemberApplicationSubmissionInput<ApplicationInput>
> => {
  const normalizedActorId = actorId.trim();

  if (!normalizedActorId) {
    throw new UserFacingError(
      "We could not check who is signed in. Sign out, sign in again, then select Create Member/Preview Invoice again."
    );
  }

  const { operationId: _untrustedOperationId, ...operationFreeInput } =
    applicationInput;
  const sourceProspectId =
    typeof operationFreeInput.sourceProspectId === "string"
      ? operationFreeInput.sourceProspectId.trim()
      : "";
  const sourceMemberId =
    typeof operationFreeInput.sourceMemberId === "string"
      ? operationFreeInput.sourceMemberId.trim()
      : "";
  const storageKey = getMemberApplicationOperationStorageKey({
    chamberId,
    sourceMemberId,
    sourceProspectId,
  });
  const operation = await getOrCreateMemberApplicationOperation({
    ...(createOperationId ? { createOperationId } : {}),
    ...(fingerprintRequest ? { fingerprintRequest } : {}),
    // The backend fingerprint includes the authenticated user. Hash the same
    // identity boundary client-side so a logout/login in this tab rotates the
    // operation instead of reusing a UUID the server must reject. The actor ID
    // itself is never persisted.
    request: {
      actorId: normalizedActorId,
      applicationInput: operationFreeInput,
    },
    ...(storage ? { storage } : {}),
    storageKey,
  });

  return {
    input: {
      ...operationFreeInput,
      operationId: operation.operationId,
    },
    operationId: operation.operationId,
    storageKey,
  };
};
